import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePortalUi } from '../../contexts/PortalUiContext';
import {
  getAllowedReferrerOrigin,
  isAllowedParentOrigin,
} from './origins';
import {
  buildRouteChangedMessage,
  parseNavigateMessage,
} from './messages';

/**
 * iframe bridge: Angular parent ↔ React Router.
 * Active only in portal embed mode (isShowSidenav === false).
 * Normal email/password sessions are unaffected.
 */
export default function PortalEmbedBridge() {
  const { isShowSidenav } = usePortalUi();
  const navigate = useNavigate();
  const location = useLocation();
  const parentOriginRef = useRef(getAllowedReferrerOrigin());
  const isEmbedMode = isShowSidenav === false;

  useEffect(() => {
    if (!isEmbedMode) return undefined;

    function onMessage(event) {
      if (!isAllowedParentOrigin(event.origin)) return;

      const parsed = parseNavigateMessage(event.data);
      if (!parsed) return;

      parentOriginRef.current = event.origin;
      navigate(parsed.path);
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [isEmbedMode, navigate]);

  useEffect(() => {
    if (!isEmbedMode) return;
    if (typeof window === 'undefined' || window.parent === window) return;

    const targetOrigin = parentOriginRef.current;
    if (!targetOrigin || !isAllowedParentOrigin(targetOrigin)) return;

    try {
      window.parent.postMessage(
        buildRouteChangedMessage(location.pathname),
        targetOrigin
      );
    } catch {
      /* parent gone / cross-origin edge */
    }
  }, [isEmbedMode, location.pathname]);

  return null;
}
